import {
  localePath,
  MARKETING_DEFAULT_LOCALE,
  MARKETING_LOCALES,
} from '#marketing/lib/marketing-locale.js';

/** @type {string} */
export const MARKETING_SITE_URL = 'https://pachub.ca';

/** @type {string} */
export const MARKETING_OG_IMAGE = `${MARKETING_SITE_URL}/og-image.png`;

/** @type {Record<string, string>} */
const OG_LOCALE_BY_MARKETING_LOCALE = {
  en: 'en_CA',
  fr: 'fr_CA',
  zh: 'zh_CN',
};

/** @type {Record<string, string>} */
const HREFLANG_BY_MARKETING_LOCALE = {
  en: 'en',
  fr: 'fr',
  zh: 'zh-Hans',
};

/**
 * @param {string} pagePath
 * @returns {string}
 */
function absoluteUrlForPagePath(pagePath) {
  return `${MARKETING_SITE_URL}${pagePath === '/' ? '' : pagePath}`;
}

/**
 * Build hreflang alternate link meta entries for a logical page.
 *
 * @param {string} pagePath - Locale-neutral path (e.g. `/features`)
 * @returns {Array<Record<string, string>>}
 */
export function marketingHreflangLinks(pagePath) {
  /** @type {Array<Record<string, string>>} */
  const links = MARKETING_LOCALES.map((locale) => ({
    tagName: 'link',
    rel: 'alternate',
    hrefLang: HREFLANG_BY_MARKETING_LOCALE[locale],
    href: absoluteUrlForPagePath(localePath(locale, pagePath)),
  }));

  links.push({
    tagName: 'link',
    rel: 'alternate',
    hrefLang: 'x-default',
    href: absoluteUrlForPagePath(localePath(MARKETING_DEFAULT_LOCALE, pagePath)),
  });

  return links;
}

/**
 * Build standard meta tags for public marketing pages.
 *
 * @param {object} params
 * @param {string} params.locale - Marketing locale (`en`, `fr`, `zh`)
 * @param {string} params.path - Full URL path including locale prefix when applicable
 * @param {string} params.pagePath - Locale-neutral path (e.g. `/features`)
 * @param {string} params.title
 * @param {string} params.description
 * @returns {Array<Record<string, string>>}
 */
export function marketingMeta({ locale, path, pagePath, title, description }) {
  const url = absoluteUrlForPagePath(path);
  const ogLocale = OG_LOCALE_BY_MARKETING_LOCALE[locale] ?? OG_LOCALE_BY_MARKETING_LOCALE.en;
  const ogAlternates = MARKETING_LOCALES.filter((entry) => entry !== locale).map(
    (entry) => OG_LOCALE_BY_MARKETING_LOCALE[entry],
  );

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: url },
    { property: 'og:image', content: MARKETING_OG_IMAGE },
    { property: 'og:locale', content: ogLocale },
    ...ogAlternates.map((content) => ({ property: 'og:locale:alternate', content })),
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: MARKETING_OG_IMAGE },
    { tagName: 'link', rel: 'canonical', href: url },
    ...marketingHreflangLinks(pagePath),
  ];
}
